import { ReactNode, useRef, useState } from "react";
import { AlertTriangle, Download, Upload } from "lucide-react";
import * as api from "../../api/utentiCassaforte";
import { AmbitoExcel, AnteprimaExcel, AnteprimaFoglioExcel, ETICHETTE_TIPO } from "../../api/utentiCassaforte";
import { getErrorMessage } from "../../api/client";
import { useToast } from "../../context/ToastContext";
import { Button } from "../../components/ui/Button";
import { Modal } from "../../components/ui/Modal";
import { excelConfermabile, excelDaApplicare } from "./confermaExcel";

/**
 * Excel «elenco utenti»: scaricare il foglio della sede (o un foglio per sede
 * del cliente) e ricaricarlo corretto.
 *
 * Il caricamento passa sempre dall'anteprima: si vede cosa cambia (nuove,
 * modificate, non toccate, in conflitto) e si conferma esplicitamente. Le
 * utenze presenti nella sede ma non nel file non si tolgono mai da sole: si
 * spuntano qui, una per una, e di partenza nessuna è spuntata. Una cella vuota
 * nel file non cancella niente. Le password del file non si leggono.
 */
export function ExcelUtenti({
  ambito,
  id,
  puoCaricare,
  onApplicato,
}: {
  ambito: AmbitoExcel;
  id: string;
  puoCaricare: boolean;
  onApplicato?: () => void;
}) {
  const { showToast } = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [scaricando, setScaricando] = useState(false);
  const [leggendo, setLeggendo] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [anteprima, setAnteprima] = useState<AnteprimaExcel | null>(null);
  const [rimuovi, setRimuovi] = useState<Set<string>>(new Set());
  const [applicando, setApplicando] = useState(false);

  async function scarica() {
    setScaricando(true);
    try {
      const { senza_casseforti: n } = await api.scaricaExcelUtenti(ambito, id);
      if (n > 0) {
        showToast(
          n === 1
            ? "Una sede senza casseforti non è nel file: le sue utenze si gestiscono dalla scheda sede."
            : `${n} sedi senza casseforti non sono nel file: le loro utenze si gestiscono dalla scheda sede.`,
          "info"
        );
      }
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile scaricare l'Excel."), "error");
    } finally {
      setScaricando(false);
    }
  }

  async function scegliFile(f: File | undefined) {
    if (input.current) input.current.value = "";
    if (!f) return;
    setLeggendo(true);
    try {
      const a = await api.anteprimaExcelUtenti(ambito, id, f);
      setFile(f);
      setAnteprima(a);
      setRimuovi(new Set());
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile leggere l'Excel."), "error");
    } finally {
      setLeggendo(false);
    }
  }

  function chiudi() {
    setFile(null);
    setAnteprima(null);
    setRimuovi(new Set());
  }

  async function conferma() {
    if (!file || !anteprima?.impronta) return;
    setApplicando(true);
    try {
      const { esiti } = await api.applicaExcelUtenti(ambito, id, file, anteprima.impronta, [...rimuovi]);
      const n = esiti.reduce((t, e) => t + e.nuove + e.modificate + e.rimosse + e.conti_tecnici + (e.accredito ? 1 : 0), 0);
      showToast(`Excel applicato: ${n} ${n === 1 ? "modifica" : "modifiche"}.`, "success");
      chiudi();
      onApplicato?.();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile applicare l'Excel."), "error");
    } finally {
      setApplicando(false);
    }
  }

  const daFare = !!anteprima && excelDaApplicare(anteprima, rimuovi);

  return (
    <>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="secondary" size="sm" loading={scaricando} onClick={scarica}>
          <Download className="h-4 w-4" aria-hidden="true" />
          Scarica Excel
        </Button>
        {puoCaricare && (
          <>
            <Button type="button" variant="secondary" size="sm" loading={leggendo} onClick={() => input.current?.click()}>
              <Upload className="h-4 w-4" aria-hidden="true" />
              Carica Excel
            </Button>
            <input
              ref={input}
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              className="hidden"
              aria-label="Excel elenco utenti da caricare"
              onChange={(e) => scegliFile(e.target.files?.[0])}
            />
          </>
        )}
      </div>

      <Modal
        open={!!anteprima}
        onClose={chiudi}
        size="lg"
        title={`Anteprima: ${anteprima?.file ?? ""}`}
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {anteprima?.impronta && !daFare && <span className="text-xs text-ink/50">Niente da applicare.</span>}
            <Button type="button" variant="ghost" onClick={chiudi}>
              Annulla
            </Button>
            <Button type="button" loading={applicando} disabled={!excelConfermabile(anteprima, rimuovi)} onClick={conferma}>
              Conferma e applica
            </Button>
          </div>
        }
      >
        {anteprima && (
          <div className="space-y-5">
            {anteprima.errori.length > 0 && (
              <div role="alert" className="rounded-lg border border-[#ef4444]/30 bg-[#ef4444]/5 px-4 py-3 text-sm text-ink">
                <p className="mb-1 flex items-center gap-2 font-semibold text-[#dc2626]">
                  <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                  Il file non si può applicare: niente verrà scritto.
                </p>
                <ul className="list-disc pl-5">
                  {anteprima.errori.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              </div>
            )}
            {anteprima.fogli.map((f) => (
              <Foglio
                key={f.sede_id}
                f={f}
                rimuovi={rimuovi}
                onRimuovi={(idUtenza, si) =>
                  setRimuovi((r) => {
                    const n = new Set(r);
                    if (si) n.add(idUtenza);
                    else n.delete(idUtenza);
                    return n;
                  })
                }
              />
            ))}
          </div>
        )}
      </Modal>
    </>
  );
}

function Gruppo({ titolo, n, children }: { titolo: string; n: number; children?: ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-xs font-bold uppercase tracking-wide text-ink/50">
        {titolo} <span className="font-normal text-ink/40">({n})</span>
      </p>
      {n > 0 && children}
    </div>
  );
}

const etichetta = (u: { tipo: keyof typeof ETICHETTE_TIPO; username: string }) => (
  <>
    <span className="font-mono font-semibold">{u.username}</span> <span className="text-ink/45">{ETICHETTE_TIPO[u.tipo]}</span>
  </>
);

function Foglio({
  f,
  rimuovi,
  onRimuovi,
}: {
  f: AnteprimaFoglioExcel;
  rimuovi: Set<string>;
  onRimuovi: (id: string, si: boolean) => void;
}) {
  return (
    <section className="space-y-3 rounded-lg border border-line p-4">
      <h3 className="text-sm font-bold text-ink">
        {f.nome_sede} <span className="font-normal text-ink/45">— foglio «{f.foglio}»</span>
      </h3>

      <Gruppo titolo="Nuove" n={f.nuove.length}>
        <ul className="space-y-0.5 text-sm">
          {f.nuove.map((u) => (
            <li key={`${u.tipo}-${u.username}`}>
              {etichetta(u)} {u.nominativo && <span>· {u.nominativo}</span>}
              {u.riapre_tolta_a_mano && <span className="text-xs text-[#c2410c]"> — era stata tolta a mano: si riapre</span>}
            </li>
          ))}
        </ul>
      </Gruppo>

      <Gruppo titolo="Modificate" n={f.modificate.length}>
        <ul className="space-y-0.5 text-sm">
          {f.modificate.map((u) => (
            <li key={u.id}>
              {etichetta(u)} · <span className="text-ink/50 line-through">{u.prima ?? "—"}</span> → {u.dopo}
            </li>
          ))}
        </ul>
      </Gruppo>

      <Gruppo titolo="Non toccate" n={f.invariate.length}>
        <p className="text-sm text-ink/60">{f.invariate.map((u) => u.username).join(", ")}</p>
      </Gruppo>

      <Gruppo titolo="In conflitto (non si applicano)" n={f.conflitti.length}>
        <ul className="space-y-0.5 text-sm text-[#c2410c]">
          {f.conflitti.map((c, i) => (
            <li key={i}>
              {c.riga !== null && <span className="text-ink/45">riga {c.riga} · </span>}
              {c.username && <span className="font-mono">{c.username} · </span>}
              {c.motivo}
            </li>
          ))}
        </ul>
      </Gruppo>

      <Gruppo titolo="Nella sede ma non nel file" n={f.assenti.length}>
        <p className="mb-1 text-xs text-ink/50">Restano, a meno di spuntarle qui.</p>
        <ul className="space-y-1 text-sm">
          {f.assenti.map((u) => (
            <li key={u.id}>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={rimuovi.has(u.id)} onChange={(e) => onRimuovi(u.id, e.target.checked)} />
                <span>
                  Togli {etichetta(u)} {u.nominativo && <span>· {u.nominativo}</span>}
                </span>
              </label>
            </li>
          ))}
        </ul>
      </Gruppo>

      {(f.conti_tecnici.length > 0 || f.accredito) && (
        <div>
          <p className="mb-1 text-xs font-bold uppercase tracking-wide text-ink/50">Conti</p>
          <ul className="space-y-0.5 text-sm">
            {f.conti_tecnici.map((c) => (
              <li key={c.device_cliente_id}>
                Conto tecnico {c.matricola}: <span className="font-mono text-ink/50 line-through">{c.prima ?? "—"}</span> →{" "}
                <span className="font-mono">{c.dopo}</span>
              </li>
            ))}
            {f.accredito && (
              <li>
                Conto di accredito: <span className="font-mono text-ink/50 line-through">{f.accredito.prima.valore ?? "—"}</span> →{" "}
                <span className="font-mono">{f.accredito.dopo.valore}</span>{" "}
                <span className="text-ink/45">({f.accredito.dopo.tipo === "iban" ? "IBAN" : "conto interno"})</span>
              </li>
            )}
          </ul>
        </div>
      )}
    </section>
  );
}
